/**
 * RecoveryJournalCheckin — Compact one-tap post-session check-in.
 * Pain (0–10) · Mobility (0–10) · Mood chip · optional note.
 * Submits to /api/healing/spinal-ease/journal, then invokes onDone.
 */

import { useState } from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const authHeader = () => {
  const token = localStorage.getItem('reclaim_token') || localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MOOD_CHIPS = [
  { key: 'radiant',  label: 'radiant',  hue: 'rgba(230,190,110,0.9)' },
  { key: 'peaceful', label: 'peaceful', hue: 'rgba(150,190,180,0.9)' },
  { key: 'hopeful',  label: 'hopeful',  hue: 'rgba(190,210,150,0.9)' },
  { key: 'tender',   label: 'tender',   hue: 'rgba(220,160,180,0.9)' },
  { key: 'weary',    label: 'weary',    hue: 'rgba(160,170,190,0.9)' },
];

const RecoveryJournalCheckin = ({ sessionId = null, source = 'post-session', onDone, onSkip }) => {
  const [pain, setPain] = useState(5);
  const [mobility, setMobility] = useState(5);
  const [mood, setMood] = useState(null);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const submit = async () => {
    if (mood === null) { setErr('Choose a mood chip first'); return; }
    setSaving(true); setErr('');
    try {
      const r = await fetch(`${API}/api/healing/spinal-ease/journal`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ pain, mobility, mood, note: note || null, session_id: sessionId, source }),
      });
      if (!r.ok) throw new Error('Save failed');
      onDone && onDone();
    } catch (e) {
      setErr(e.message);
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.8, ease: 'easeOut' }}
      className="w-full max-w-md rounded-3xl p-6 mb-6"
      style={{
        background: 'linear-gradient(180deg, rgba(24,22,18,0.95) 0%, rgba(14,12,10,0.95) 100%)',
        border: '1px solid rgba(200,170,110,0.16)',
      }}
      data-testid="journal-checkin"
    >
      <p className="text-white/25 text-[10px] tracking-[0.3em] uppercase mb-1 text-center" style={{ fontFamily: "'Manrope', sans-serif" }}>
        Recovery Journal
      </p>
      <h3 className="text-white/80 text-lg mb-5 text-center" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
        How does it feel now?
      </h3>

      {/* Pain slider */}
      <div className="mb-5">
        <div className="flex items-baseline justify-between mb-2">
          <label className="text-white/55 text-xs tracking-[0.15em] uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>Pain</label>
          <span className="text-white/85 text-lg" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>{pain}<span className="text-white/30 text-xs">/10</span></span>
        </div>
        <input
          type="range" min={0} max={10} step={1} value={pain}
          onChange={(e) => setPain(parseInt(e.target.value))}
          className="w-full journal-slider journal-slider-pain"
          data-testid="journal-pain"
        />
      </div>

      {/* Mobility slider */}
      <div className="mb-5">
        <div className="flex items-baseline justify-between mb-2">
          <label className="text-white/55 text-xs tracking-[0.15em] uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>Mobility</label>
          <span className="text-white/85 text-lg" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>{mobility}<span className="text-white/30 text-xs">/10</span></span>
        </div>
        <input
          type="range" min={0} max={10} step={1} value={mobility}
          onChange={(e) => setMobility(parseInt(e.target.value))}
          className="w-full journal-slider journal-slider-mobility"
          data-testid="journal-mobility"
        />
      </div>

      {/* Mood chips */}
      <div className="mb-5">
        <label className="block text-white/55 text-xs tracking-[0.15em] uppercase mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>Mood</label>
        <div className="flex flex-wrap gap-2">
          {MOOD_CHIPS.map((m) => (
            <motion.button
              key={m.key}
              onClick={() => setMood(m.key)}
              whileTap={{ scale: 0.94 }}
              className="px-4 py-1.5 rounded-full text-xs"
              style={{
                background: mood === m.key ? 'rgba(200,170,110,0.18)' : 'rgba(255,255,255,0.04)',
                border: `1px solid ${mood === m.key ? m.hue : 'rgba(255,255,255,0.1)'}`,
                color: mood === m.key ? m.hue : 'rgba(255,255,255,0.6)',
                fontFamily: "'Playfair Display', serif",
                fontStyle: 'italic',
              }}
              data-testid={`journal-mood-${m.key}`}
            >
              {m.label}
            </motion.button>
          ))}
        </div>
      </div>

      {/* Optional note */}
      <div className="mb-5">
        <input
          type="text"
          placeholder="A word or two, if you'd like"
          value={note}
          onChange={(e) => setNote(e.target.value.slice(0, 200))}
          className="w-full px-3 py-2 rounded-lg text-sm text-white/80 outline-none"
          style={{
            background: 'rgba(255,255,255,0.04)',
            border: '1px solid rgba(255,255,255,0.08)',
            fontFamily: "'Manrope', sans-serif",
          }}
          data-testid="journal-note"
        />
      </div>

      {err && (
        <p className="text-center text-xs text-red-300/80 mb-3" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {err}
        </p>
      )}

      <div className="flex gap-3">
        {onSkip && (
          <button
            onClick={onSkip}
            className="flex-1 py-2.5 rounded-full text-xs text-white/40"
            style={{
              background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.06)',
              fontFamily: "'Manrope', sans-serif",
            }}
            data-testid="journal-skip"
          >
            Skip
          </button>
        )}
        <motion.button
          onClick={submit}
          disabled={saving || mood === null}
          whileTap={{ scale: 0.97 }}
          className="flex-[2] flex items-center justify-center gap-2 py-2.5 rounded-full text-sm disabled:opacity-40"
          style={{
            background: 'rgba(180,210,160,0.14)',
            border: '1px solid rgba(180,210,160,0.28)',
            color: 'rgba(220,240,200,0.9)',
            fontFamily: "'Manrope', sans-serif",
          }}
          data-testid="journal-save"
        >
          <Check size={14} />
          {saving ? 'Saving...' : 'Save to journal'}
        </motion.button>
      </div>
    </motion.div>
  );
};

export default RecoveryJournalCheckin;
